import React, { useState } from 'react';
import { Container, Row, Col, Spinner, Button } from 'reactstrap';
import {
  useTranslator,
  useToast,
  withMobileAppMode,
  ActiveProductsConnectorApollo,
  StripePortalSessionConnectorApollo,
} from '@front-activerse/common-components';
import type {
  ActiveProductsData,
  GetStripePortalSessionUrlFn,
} from '@front-activerse/common-components';
import Layout from '../Layout/Layout';
import TitleBar from '../Layout/TitleBar';
import TrackerNoAccess from '../Tracker/NoAccess/TrackerNoAccess';
import AccessCard from './AccessCard';
import AccessCancellationInfo from './AccessCancellationInfo';
import { mapActiveProducts } from './helpers';
import { withDevAccessFixture } from './devAccessFixture';

interface Props {
  /** Absolute URL Stripe sends the user back to after the Billing Portal; normally this page. */
  returnUrl: string;
}

interface InnerProps extends Props {
  mobileAppMode?: boolean;
  loading: boolean;
  error: boolean;
  data: ActiveProductsData | null;
  refetch: () => void;
  getPortalSessionUrl: GetStripePortalSessionUrlFn;
}

const AccessShell: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Layout>
    <TitleBar showBackBtn={false}>{title}</TitleBar>
    <section className="page-template-access full-height3a">
      <Container className="py-xl">
        <Row>
          <Col md={{ size: 8, offset: 2 }}>{children}</Col>
        </Row>
      </Container>
    </section>
  </Layout>
);

const AccessInner: React.FC<InnerProps> = ({
  mobileAppMode = false,
  loading,
  error,
  data,
  refetch,
  getPortalSessionUrl,
  returnUrl,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const [managingSubscriptionId, setManagingSubscriptionId] = useState<number | null>(null);

  const title = t('access.navbarTitle');

  if (loading) {
    return (
      <AccessShell title={title}>
        <div className="d-flex justify-content-center py-xxl">
          <Spinner />
        </div>
      </AccessShell>
    );
  }

  if (error || !data) {
    return (
      <AccessShell title={title}>
        <div className="text-center py-xxl" data-testid="access-error">
          <p className="text-text-variant-color">{t('universalError.title')}</p>
          <Button color="primary" outline onClick={() => refetch()}>
            {t('universalError.tryAgain.button')}
          </Button>
        </div>
      </AccessShell>
    );
  }

  const products = mapActiveProducts(data);

  if (products.length === 0) {
    return <TrackerNoAccess showBackBtn={false} />;
  }

  const handleManage = async (subscriptionId: number) => {
    if (managingSubscriptionId !== null) return;

    setManagingSubscriptionId(subscriptionId);
    const url = await getPortalSessionUrl(subscriptionId, returnUrl);

    if (!url) {
      setManagingSubscriptionId(null);
      toastError();
      return;
    }

    // Full-page navigation: the Stripe Billing Portal brings the user back to `returnUrl`.
    window.location.assign(url);
  };

  return (
    <AccessShell title={title}>
      <h2
        className="title-medium semi-bold access-section-title"
        data-testid="access-active-products-title"
      >
        {t('access.activeProducts.title')}
      </h2>
      <div className="access-card-list">
        {products.map((product) => (
          <React.Fragment key={product.key}>
            <AccessCard
              product={product}
              showManageButton={!mobileAppMode}
              isManaging={managingSubscriptionId === product.subscriptionId}
              onManage={handleManage}
            />
            {product.cancellationInfoKey && (
              <AccessCancellationInfo infoKey={product.cancellationInfoKey} />
            )}
          </React.Fragment>
        ))}
      </div>
    </AccessShell>
  );
};

const AccessInnerWithMobileAppMode = withMobileAppMode(AccessInner);

const AccessContainer: React.FC<Props> = ({ returnUrl }) => (
  <ActiveProductsConnectorApollo>
    {(result) => {
      // Developer options can swap in a canned variant on non-production builds.
      const { loading, error, data, refetch } = withDevAccessFixture(result);
      return (
        <StripePortalSessionConnectorApollo>
          {({ getPortalSessionUrl }) => (
            <AccessInnerWithMobileAppMode
              loading={loading}
              error={error}
              data={data}
              refetch={refetch}
              getPortalSessionUrl={getPortalSessionUrl}
              returnUrl={returnUrl}
            />
          )}
        </StripePortalSessionConnectorApollo>
      );
    }}
  </ActiveProductsConnectorApollo>
);

export default AccessContainer;
